import { InfoList } from "react-migration/components/InfoList";
import PropertySaleValueQuery from "./PropertySaleValueQuery.gql";
import { useQuery } from "@apollo/client";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo/routedClient";
import { Currency, formatToCurrency } from "react-migration/lib/util/numberFormat";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useId, useMemo } from "react";
import { dateString } from "src/js/util/dates";
import { userStore } from "src/js/stores/user/store";

interface PropertySaleValue {
  _id: string;
  currency: Currency;
  date_of_transfer: string;
  price: number;
  property_id: string;
}

interface PropertySaleValueProps {
  uprn: string;
}

export function useSummaryPriceData({ uprn, skip }: Readonly<PropertySaleValueProps & { skip?: boolean }>) {
  const { loading, data, error } = useQuery<{ comparablesByUPRN: PropertySaleValue[] }>(PropertySaleValueQuery, {
    variables: { uprn },
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Gateway,
    },
    skip,
  });
  const salesData = useAllSales({ sales: data?.comparablesByUPRN, sortBy: sortSalesByDate }) || [];
  return { salesData, loading, error };
}

export function PropertySaleValue({ uprn }: Readonly<PropertySaleValueProps>) {
  const { t } = useTranslation();
  const labelId = useId();
  const { loading, data } = useQuery<{ comparablesByUPRN: PropertySaleValue[] }>(PropertySaleValueQuery, {
    variables: { uprn },
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Gateway,
    },
  });

  return (
    <InfoList.Item>
      <InfoList.ItemTitle id={labelId}>{t("ownership.react.map.sidebar.property_info.value_title")}</InfoList.ItemTitle>

      <InfoList.ItemValue aria-labelledby={labelId}>
        {loading ? (
          <span className="italic text-neutral-500">
            {t("ownership.react.map.sidebar.property_info.value_loading")}
          </span>
        ) : (
          <Price sales={data?.comparablesByUPRN} sortBy={sortSalesByDate} />
        )}
      </InfoList.ItemValue>
    </InfoList.Item>
  );
}

function sortSalesByDate(a: PropertySaleValue, b: PropertySaleValue) {
  return new Date(b.date_of_transfer).getTime() - new Date(a.date_of_transfer).getTime();
}

interface PriceProps {
  sales?: PropertySaleValue[];
  sortBy: (a: PropertySaleValue, b: PropertySaleValue) => number;
}

function useAllSales({ sales, sortBy }: Readonly<PriceProps>) {
  const { locale } = useSnapshot(userStore);

  const allSales = useMemo(() => {
    const sortedTransactions = sales?.sort(sortBy);
    if (!sortedTransactions?.length) return [];
    try {
      return sortedTransactions.map((transaction) =>
        transaction.price && transaction.date_of_transfer
          ? {
              price: formatToCurrency({
                amount: transaction.price,
                currency: transaction.currency,
                locale,
              }),
              date: dateString(transaction.date_of_transfer, locale),
            }
          : undefined
      );
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [locale, sales, sortBy]);
  return allSales;
}

function Price({ sales, sortBy }: Readonly<PriceProps>) {
  const { t } = useTranslation();
  const { price, date } = useAllSales({ sales, sortBy })[0] || {};
  if (!date || !price) return <span>{t("ownership.react.map.sidebar.property_info.value_unavailable")}</span>;

  return (
    <span>
      {t("ownership.react.map.sidebar.property_info.value", {
        price,
        date,
      })}
    </span>
  );
}
